import { useState, useEffect, useCallback } from "react";
import { Checkbox, Input, Button, message, Typography, Space } from "antd";
import { SearchOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import DataTable from "../../../components/common/DataTable";
import FilterBar from "../../../components/common/FilterBar";
import PageLoader from "../../../components/common/PageLoader";
import usePermission from "../../../hooks/usePermission";
import { listSettings } from "../../../services/settings.service";
import {
  listAdminUsers,
  getAdminUser,
  updateZoneMapping,
} from "../../../services/adminUsers.service";

const { Text } = Typography;

const VIEW_LIST = "list";
const VIEW_EDIT = "edit";

export default function ZoneMappingTab() {
  const { canEdit } = usePermission("usersLeaves");

  const [view, setView] = useState(VIEW_LIST);
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [cities, setCities] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [expandedCities, setExpandedCities] = useState([]);
  const [zoneSearch, setZoneSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [listSearch, setListSearch] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, czRes] = await Promise.all([
        listAdminUsers({ page, per_page: pageSize, search: listSearch }),
        listSettings("city-zones", { per_page: 200 }),
      ]);
      setUsers(usersRes.records || []);
      setTotalCount(usersRes.pagination?.total_count || 0);
      const allRecords = czRes.records || [];
      setCities(allRecords.filter((r) => r.entityType === "city"));
      setZones(allRecords.filter((r) => r.entityType === "zone"));
    } catch {
      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, listSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getZonesForCity = (cityId) =>
    zones.filter((z) => z.parentCity === cityId);

  const filteredCities = zoneSearch
    ? cities.filter((c) =>
        c.name.toLowerCase().includes(zoneSearch.toLowerCase()) ||
        getZonesForCity(c._id).some((z) =>
          z.name.toLowerCase().includes(zoneSearch.toLowerCase())
        )
      )
    : cities;

  const toggleCity = (cityId) => {
    setExpandedCities((prev) =>
      prev.includes(cityId)
        ? prev.filter((id) => id !== cityId)
        : [...prev, cityId]
    );
  };

  const toggleZone = (zoneId) => {
    setSelectedZones((prev) =>
      prev.includes(zoneId)
        ? prev.filter((id) => id !== zoneId)
        : [...prev, zoneId]
    );
  };

  const toggleAllZonesForCity = (cityId, checked) => {
    const cityZoneIds = getZonesForCity(cityId).map((z) => z._id);
    setSelectedZones((prev) => {
      if (checked) {
        return [...new Set([...prev, ...cityZoneIds])];
      }
      return prev.filter((id) => !cityZoneIds.includes(id));
    });
  };

  const selectAll = (checked) => {
    if (checked) {
      setSelectedZones(zones.map((z) => z._id));
    } else {
      setSelectedZones([]);
    }
  };

  const handleEdit = async (record) => {
    setEditingUser(record);
    setView(VIEW_EDIT);
    setZoneSearch("");
    setExpandedCities([]);
    try {
      const user = await getAdminUser(record._id);
      setSelectedZones(user.zoneMapping || []);
    } catch {
      message.error("Failed to load zone mapping");
    }
  };

  const handleBackToList = () => {
    setView(VIEW_LIST);
    setEditingUser(null);
    setSelectedZones([]);
    setZoneSearch("");
    setExpandedCities([]);
  };

  const handleSave = async () => {
    if (!editingUser?._id) return;
    setSaving(true);
    try {
      await updateZoneMapping(editingUser._id, selectedZones);
      message.success("Zone mapping updated");
      await fetchData();
      handleBackToList();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading && users.length === 0) {
    return <PageLoader tip="Loading zone mapping..." />;
  }

  const allSelected = zones.length > 0 && selectedZones.length === zones.length;

  if (view === VIEW_LIST) {
    const columns = [
      {
        title: "Name",
        key: "name",
        render: (_, record) => (
          <Text strong style={{ fontSize: 13 }}>
            {record.firstName} {record.lastName}
          </Text>
        ),
      },
      { title: "Email", dataIndex: "email" },
      { title: "Role", dataIndex: "role", width: 80 },
      {
        title: "Assigned Zones",
        key: "zoneCount",
        render: (_, record) => {
          const count = record.zoneMapping?.length || 0;
          return count > 0 ? `${count} zone${count !== 1 ? "s" : ""}` : "None";
        },
      },
    ];

    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <Text strong style={{ fontSize: 16 }}>Zone Mapping</Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>Assign users to geographic regions</Text>
          </div>
        </div>

        <FilterBar
          searchPlaceholder="Search by name or email..."
          onSearch={(val) => { setListSearch(val); setPage(1); }}
        />

        <DataTable
          columns={columns}
          dataSource={users}
          loading={loading}
          pagination={{ current: page, pageSize, total: totalCount }}
          onPageChange={(p) => setPage(p)}
          actions={{
            onEdit: canEdit ? handleEdit : null,
            canEdit,
          }}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>Zone Mapping</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {editingUser
              ? `Editing: ${editingUser.firstName} ${editingUser.lastName}`
              : "Assign user to geographic regions"}
          </Text>
        </div>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBackToList}>
            Back to Listing
          </Button>
          {canEdit && (
            <Button type="primary" onClick={handleSave} loading={saving}>
              Save Changes
            </Button>
          )}
        </Space>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderBottom: "1px solid #f0f0f0" }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search city or zone..."
            value={zoneSearch}
            onChange={(e) => setZoneSearch(e.target.value)}
            style={{ maxWidth: 260 }}
            allowClear
          />
          {canEdit && (
            <Checkbox checked={allSelected} onChange={(e) => selectAll(e.target.checked)}>
              <Text style={{ fontSize: 12, fontWeight: 600 }}>Select All</Text>
            </Checkbox>
          )}
        </div>

        {filteredCities.map((city) => {
          const cityZones = getZonesForCity(city._id);
          const isExpanded = expandedCities.includes(city._id);
          const checkedCount = cityZones.filter((z) => selectedZones.includes(z._id)).length;
          const allCityChecked = cityZones.length > 0 && checkedCount === cityZones.length;

          return (
            <div key={city._id} style={{ borderBottom: "1px solid #f0f0f0" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 20px",
                  background: "#f9fafb",
                  cursor: "pointer",
                }}
                onClick={() => toggleCity(city._id)}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Checkbox
                    checked={allCityChecked}
                    indeterminate={checkedCount > 0 && !allCityChecked}
                    disabled={!canEdit}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleAllZonesForCity(city._id, e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Text strong style={{ fontSize: 12 }}>{city.name}</Text>
                  <span style={{
                    fontSize: 10,
                    color: "#6b7280",
                    border: "1px solid #e0e0e0",
                    background: "#fff",
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontWeight: 600,
                  }}>
                    {cityZones.length} zone{cityZones.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: "#9ca3af" }}>
                  {isExpanded ? "−" : "+"}
                </span>
              </div>

              {isExpanded && (
                <div style={{ padding: "10px 20px 14px 46px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {cityZones.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: 12 }}>No zones defined</Text>
                  ) : (
                    cityZones.map((zone) => (
                      <Checkbox
                        key={zone._id}
                        checked={selectedZones.includes(zone._id)}
                        disabled={!canEdit}
                        onChange={() => toggleZone(zone._id)}
                        style={{ fontSize: 12 }}
                      >
                        {zone.name}
                      </Checkbox>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div style={{
          padding: "12px 20px",
          fontSize: 10,
          color: "#9ca3af",
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          borderTop: "1px solid #f5f5f5",
        }}>
          Total Active Areas: {selectedZones.length}
        </div>
      </div>
    </div>
  );
}
